import axios from 'axios';

/**
 * Normalizza le risposte degli ordini
 */
const normalizeOrdersResponse = (response) => {
    if (!response) {
        return { success: false, orders: [], pagination: null };
    }

    if (response.success && response.data) {
        return {
            success: true,
            orders: response.data.orders || [],
            pagination: response.data.pagination
        };
    }

    if (response.orders) {
        return {
            success: true,
            orders: response.orders,
            pagination: response.pagination
        };
    }

    return { success: false, orders: [], pagination: null };
};

/**
 * Normalizza le risposte degli indirizzi
 */
const normalizeAddressesResponse = (response) => {
    if (!response) {
        return { success: false, addresses: [] };
    }

    if (response.success && response.addresses) {
        return { success: true, addresses: response.addresses };
    }

    if (response.addresses) {
        return { success: true, addresses: response.addresses };
    }

    if (response.data && Array.isArray(response.data)) {
        return { success: true, addresses: response.data };
    }

    return { success: false, addresses: [] };
};

const authConfig = {
    withCredentials: true
};

export const profileService = {

    // ============ PROFILO ============
    getProfile: async () => {
        try {
            const response = await axios.get('/api/users/profile', authConfig);
            return {
                success: response.data.success,
                user: response.data.user,
                message: response.data.message
            };
        } catch (error) {
            console.error('Get profile error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore caricamento profilo'
            };
        }
    },


    updateProfile: async (profileData) => {
        try {
            const response = await axios.put('/api/users/profile', profileData, authConfig); 
            return response.data;
        } catch (error) {
            console.error('Update profile error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore aggiornamento profilo'
            };
        }
    },

    changePassword: async (passwordData) => {
        try {
            const response = await axios.post('/api/users/change-password', passwordData, authConfig);
            return response.data;
        } catch (error) {
            console.error('Errore cambio password:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore cambio password'
            };
        }
    },

    // ============ AVATAR ============
    uploadAvatar: async (file) => {
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const response = await axios.post('/api/users/avatar', formData, {
                ...authConfig,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return {
                success: response.data.success,
                user: response.data.user,
                message: response.data.message
            };
        } catch (error) {
            console.error('Upload error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore durante l\'upload'
            };
        }
    },

    removeAvatar: async () => {
        try {
            const response = await axios.delete('/api/users/avatar', authConfig);
            return {
                success: response.data.success,
                user: response.data.user,
                message: response.data.message
            };
        } catch (error) {
            console.error('Remove avatar error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore durante la rimozione'
            };
        }
    },


    getAvatarUrl: (avatarUrl) => {
    // ✅ Se null, undefined, o stringa vuota
        if (!avatarUrl || avatarUrl === 'null' || avatarUrl === 'undefined') {
            console.log('📸 Nessun avatar URL valido, uso default');
            return null;
        }
        
        // ✅ Se è già un URL completo (http/https)
        if (avatarUrl.startsWith('http')) {
            return avatarUrl;
        }
        
        // ✅ Se è un percorso assoluto dal server (inizia con /)
        if (avatarUrl.startsWith('/')) {
            return avatarUrl;
        }
        
        // ✅ Se è solo un nome file, aggiungi il percorso uploads
        return `/uploads/${avatarUrl}`;
    },

    // ============ INDIRIZZI ============
    getAddresses: async () => {
        try {
            const response = await axios.get('/api/users/addresses', authConfig);
            return normalizeAddressesResponse(response.data);
        } catch (error) {
            console.error('Errore recupero indirizzi:', error);
            return { success: false, addresses: [] };
        }
    },

    createAddress: async (addressData) => {
        try {
            const response = await axios.post('/api/users/addresses', addressData, authConfig);
            return response.data;
        } catch (error) {
            console.error('Errore creazione indirizzo:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore creazione indirizzo'
            };
        }
    },

    updateAddress: async (addressId, addressData) => {
        try {
            const response = await axios.put(`/api/users/addresses/${addressId}`, addressData, authConfig);
            return response.data;
        } catch (error) {
            console.error('Errore aggiornamento indirizzo:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore aggiornamento indirizzo'
            };
        }
    },

    deleteAddress: async (addressId) => {
        try {
            const response = await axios.delete(`/api/users/addresses/${addressId}`, authConfig);
            return response.data;
        } catch (error) {
            console.error('Errore eliminazione indirizzo:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore eliminazione indirizzo'
            };
        }
    },

    setDefaultAddress: async (addressId) => {
        try {
            const response = await axios.patch(
                `/api/users/addresses/${addressId}/set-default`,
                {},
                authConfig
            );
            return response.data;
        } catch (error) {
            console.error('❌ Errore impostazione indirizzo predefinito:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore impostazione indirizzo predefinito'
            };
        }
    },

     // ============ ORDINI ============
    getUserOrders: async (page = 1, limit = 10) => {
        try {
            const response = await axios.get('/api/orders/user', {
                ...authConfig,  // ✅ AGGIUNTO
                params: { page, limit }
            });
            return normalizeOrdersResponse(response.data);
        } catch (error) {
            console.error('Errore recupero ordini:', error);
            return { success: false, orders: [], pagination: null };
        }
    },

    getOrderDetails: async (orderId) => {
        try {
            const response = await axios.get(`/api/orders/${orderId}`, authConfig);
            return response.data;
        } catch (error) {
            console.error('Errore recupero dettaglio ordine:', error);
            return { success: false, order: null };
        }
    },

    cancelOrder: async (orderId) => {
        try {
            const response = await axios.put(`/api/orders/${orderId}/cancel`, {}, authConfig);
            return response.data;
        } catch (error) {
            console.error('Errore annullamento ordine:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Errore annullamento ordine'
            };
        }
    },

    trackOrder: async (orderId) => {
        try {
            const response = await axios.get(`/api/orders/${orderId}/tracking`, authConfig);
            return response.data;
        } catch (error) {
            console.error('Errore recupero tracking:', error);
            return { success: false, tracking: null };
        }
    },

    // ============ UTILITY FUNCTIONS ============
    formatPrice: (price) => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR'
        }).format(price || 0);
    },

    formatDate: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    },

    formatShortDate: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit'
        });
    },

    getOrderStatus: (status) => {
        const statusMap = {
            'in_attesa': { label: 'In Attesa', color: '#ff9800', icon: '⏳', class: 'status-pending' },
            'confermato': { label: 'Confermato', color: '#2196f3', icon: '✅', class: 'status-confirmed' },
            'spedito': { label: 'Spedito', color: '#3f51b5', icon: '🚚', class: 'status-shipped' },
            'consegnato': { label: 'Consegnato', color: '#4caf50', icon: '🏠', class: 'status-delivered' },
            'annullato': { label: 'Annullato', color: '#f44336', icon: '❌', class: 'status-cancelled' }
        };
        return statusMap[status] || { label: status, color: '#999', icon: '❓', class: 'status-unknown' };
    },

    getAddressType: (type) => {
        const typeMap = {
            'spedizione': { label: 'Spedizione', icon: '🚚' },
            'fatturazione': { label: 'Fatturazione', icon: '📄' }
        };
        return typeMap[type] || { label: type, icon: '📍' };
    },

    getInitials: (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    }
};

export default profileService;
